import { ArrowUpRightIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useCallback } from 'react';

import { useSettingsDialog } from '@/components/settings/SettingsDialogProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIntegrations } from '@/contexts/IntegrationContext';
import { POSTABLE_PLATFORMS, PLATFORMS as REGISTRY_PLATFORMS } from '@/lib/platforms/registry';
import { SIDEBAR_POPOVER_ICON, SIDEBAR_POPOVER_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import type { PlatformType } from '@/services/socialAuth/types';
import { getPlatformIcon } from '@/utils/platformUtils';

const PLATFORMS = POSTABLE_PLATFORMS.map((id) => ({
  id,
  label: REGISTRY_PLATFORMS[id].label,
  oauthReady: REGISTRY_PLATFORMS[id].oauthReady,
}));

// Mobile bumped via SIDEBAR_POPOVER_LABEL (ui-tokens.ts) to match the sidebar
// row's own mobile scale — desktop stays text-caption, per the "one system"
// popover-row contract documented in app-sidebar.tsx.
const ROW_LABEL = cn(SIDEBAR_POPOVER_LABEL, 'md:text-caption');
const SWITCH_CLS = 'data-[state=checked]:bg-foreground/80 data-[state=unchecked]:bg-muted scale-90';

export interface PlatformPlugMenuProps {
  /** The currently-selected platforms on whatever entity owns them (post, draft, etc.). */
  platforms: string[];
  /** Called when the user toggles a connected platform on/off (attach/detach). */
  onTogglePlatform: (platformId: string) => void;
  /**
   * Opt-in enable/disable mode. When BOTH are provided (PostBody passes them,
   * StudioCard does not — so its behavior is unchanged), an already-attached
   * platform's Switch means "active for the next send" rather than attach, a
   * trailing × detaches it, and a paused row dims. `disabledPlatforms` is the
   * attached-but-paused set (from `media_keys.disabledPlatforms`).
   */
  disabledPlatforms?: string[];
  onToggleEnabled?: (platformId: string) => void;
  /**
   * Unconnected platforms currently toggled for a LOCAL, unsaved preview —
   * never written to `platforms`/the post, never eligible to send. Lets a
   * creator see what their content would look like on a platform before
   * committing to connect it. Omit (with `onTogglePreview`) to hide this
   * control entirely — PostBody is the only caller that wires it up today.
   */
  previewPlatforms?: string[];
  /** Toggles an unconnected platform's local preview on/off. */
  onTogglePreview?: (platformId: string) => void;
  /** Anything renderable. The plug menu will wrap it as a DropdownMenuTrigger. */
  trigger: ReactNode;
  /** Radix align for the popover. Defaults to 'end'. */
  align?: 'start' | 'center' | 'end';
  /** Radix side. Defaults to 'bottom'. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Optional className passed to DropdownMenuContent. */
  contentClassName?: string;
}

/**
 * Reusable platform-selection plug. Surfaces the same Connected-accounts pattern
 * from PromptSettingsMenu wherever a user might think "I want this on more
 * platforms" — post detail dialogs, generated image cards, etc.
 *
 * Uses IntegrationContext as the single source of truth for connection status.
 * Connect-flow goes through the same connectPlatform that the rest of the app
 * uses (no fork). Toggling a connected platform is a parent concern — passed
 * through onTogglePlatform.
 */
export function PlatformPlugMenu({
  platforms,
  onTogglePlatform,
  disabledPlatforms,
  onToggleEnabled,
  previewPlatforms,
  onTogglePreview,
  trigger,
  align = 'end',
  side = 'bottom',
  contentClassName,
}: PlatformPlugMenuProps) {
  const enableMode = typeof onToggleEnabled === 'function';
  const previewMode = typeof onTogglePreview === 'function';
  const previewSet = new Set(previewPlatforms ?? []);
  const disabledSet = new Set(disabledPlatforms ?? []);
  const { connectedAccounts, connectedPlatforms } = useIntegrations();
  const { openSettings } = useSettingsDialog();

  // Deep-link into Settings instead of calling connectPlatform/connectFunction
  // directly. Phase B of OAuth (oauth-finalize-with-account) only runs from
  // useOAuthConnection's window "message" listener, and that hook is only
  // ever mounted by IntegrationConnectManager, which only renders inside
  // Settings surfaces — this menu lives in the composer, nowhere near it.
  // Calling connectPlatform here opened the OAuth popup and let it complete
  // successfully with the provider, then posted its result into a void with
  // zero listeners: no modal, no error, no saved connection, every single
  // time. Same class of bug fixed in ConnectedAccountsList (see its
  // handleReconnect) and IntegrationSettings' detail view.
  //
  // Dispatches the raw event (not the useSettingsDialog openSettings() below,
  // which only takes a bare section) so the platform — and, when we already
  // resolved the connected row, its accountId — rides along as `highlight`,
  // same contract as AnalyticsFiltersPopover/AnalyticsAccountsPopover's
  // openIntegrations. Without it every Connect/Reconnect click here landed on
  // the generic, unfiltered Connections list instead of the row clicked.
  const handleConnect = useCallback((id: PlatformType, accountId?: string) => {
    window.dispatchEvent(
      new CustomEvent('mora:openSettings', {
        detail: { section: 'integrations', highlight: id, accountId },
      }),
    );
  }, []);

  return (
    // This is a multi-select editor, not a command menu. A modal Radix menu
    // keeps its focus/interaction layer active after preventDefault(), so the
    // first click into the caption editor is consumed as an outside dismissal
    // and can cascade into the Inspector's own dismiss handling. Non-modal
    // keeps repeated selections available while allowing the next outside
    // click to focus the editor normally.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={8}
        className={cn(
          'w-60 rounded-xl border border-border/60 bg-popover/95 backdrop-blur-xl shadow-elevation-3',
          contentClassName,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {PLATFORMS.map((p) => {
          const Icon = getPlatformIcon(p.id as PlatformType);
          const connected = connectedPlatforms.includes(p.id);
          const selected = platforms.includes(p.id);

          // The specific account this platform posts to (handle / page name).
          // Surfaced as a muted subline so "which account?" is always answerable
          // without a hidden tooltip — the user shouldn't have to guess where a
          // scheduled post will land. A connected account carrying an `error`
          // (expired/revoked token) is connected-but-not-synced: posting would
          // silently fail, so we flag it for reconnect instead of pretending
          // it's healthy.
          const account = connectedAccounts.find((a) => a.platform === p.id) ?? null;
          const accountHandle = account?.displayName || account?.identifier || null;
          const needsReconnect = connected && !!account?.error;
          // Enable-mode state (PostBody only): an attached platform can be
          // paused for the next send without detaching (keeps its caption/crop).
          const paused = enableMode && selected && disabledSet.has(p.id);

          if (!p.oauthReady) {
            return (
              <DropdownMenuItem key={p.id} disabled className="justify-between gap-2 opacity-60">
                <span className="flex items-center gap-2">
                  <Icon className="size-4" tone="muted" />
                  <span className={ROW_LABEL}>{p.label}</span>
                </span>
                <span className="text-detail text-muted-foreground">Soon</span>
              </DropdownMenuItem>
            );
          }

          if (!connected) {
            const previewing = previewMode && previewSet.has(p.id);
            return (
              <DropdownMenuItem
                key={p.id}
                onSelect={(e) => {
                  e.preventDefault();
                  handleConnect(p.id as PlatformType);
                }}
                className="justify-between gap-2"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Icon className={cn('size-4 shrink-0', previewing && 'opacity-40')} />
                  <span className="flex min-w-0 flex-col">
                    <span className={cn(ROW_LABEL, previewing && 'opacity-50')}>{p.label}</span>
                    {previewing ? (
                      <span className="truncate text-detail text-muted-foreground/70">
                        Previewing — not posting
                      </span>
                    ) : null}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {previewMode ? (
                    <Tooltip>
                      {/* The trigger's `asChild` merges Radix Tooltip's OWN
                          data-state (open/closed) onto whatever child it
                          wraps — Switch already owns that same attribute for
                          checked/unchecked, and one clobbers the other on the
                          shared DOM node. Wrapping a plain span as the merge
                          target keeps Tooltip's data-state off the Switch
                          entirely, so its own checked/unchecked styling
                          (background, border) actually applies. Without this
                          the switch rendered with a transparent background
                          and a 0px border — present, clickable, and totally
                          invisible (verified live, 2026-09-16). */}
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Switch
                            checked={previewing}
                            onCheckedChange={() => onTogglePreview!(p.id)}
                            className={SWITCH_CLS}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Preview ${p.label} — not connected, won't post`}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        See how this post would look on {p.label}. Connect to actually send it.
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                  <span className="text-detail font-medium text-foreground/70">Connect</span>
                </span>
              </DropdownMenuItem>
            );
          }

          return (
            <DropdownMenuItem
              key={p.id}
              onSelect={(e) => {
                e.preventDefault();
                // A broken connection re-opens the connect flow instead of
                // toggling — toggling it on would queue a post that can't send.
                if (needsReconnect) {
                  handleConnect(p.id as PlatformType, account?.accountId);
                  return;
                }
                // Enable-mode: clicking an attached row toggles paused/active
                // (detach is the trailing ×). Not-yet-attached rows still attach.
                if (enableMode && selected) onToggleEnabled!(p.id);
                else onTogglePlatform(p.id);
              }}
              className="justify-between gap-2"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Icon className={cn('size-4 shrink-0', paused && 'opacity-40')} />
                <span className="flex min-w-0 flex-col">
                  <span className={cn(ROW_LABEL, paused && 'opacity-50')}>{p.label}</span>
                  {paused ? (
                    <span className="truncate text-detail text-muted-foreground/70">
                      Paused for this post
                    </span>
                  ) : needsReconnect ? (
                    // Subline is just "Not synced" (the amber state); the
                    // single "Reconnect" CTA on the right IS the call to action.
                    // Saying "reconnect" in both places was redundant AND
                    // truncated to "…recon" in the w-60 menu (canon:
                    // ConnectedIntegrationRow — "the word is the CTA, not
                    // decoration").
                    <span className="inline-flex items-center gap-1 truncate text-detail text-warning">
                      <WarningCircleIcon className="size-3 shrink-0" weight="fill" />
                      Not synced
                    </span>
                  ) : accountHandle ? (
                    <span className="truncate text-detail text-muted-foreground/70">
                      {accountHandle}
                    </span>
                  ) : null}
                </span>
              </span>
              {needsReconnect ? (
                <span className="shrink-0 text-detail font-medium text-warning">Reconnect</span>
              ) : (
                <span className="flex shrink-0 items-center gap-1.5">
                  {/* Detach (true remove) vs the Switch's pause — a real,
                      distinct action (loses this platform's caption/crop
                      overrides; pausing keeps them), but sitting next to an
                      identical-looking toggle with only a hover `title` read
                      as pure redundancy (founder review). A divider plus a
                      real Tooltip on the one non-obvious control — a Switch
                      needs no explanation, an × next to a Switch does. */}
                  {enableMode && selected ? (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Remove ${p.label} from this post`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onTogglePlatform(p.id);
                            }}
                            className="inline-flex size-6 items-center justify-center rounded-full text-muted-foreground/50 transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <XIcon className={SIDEBAR_POPOVER_ICON} weight="regular" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          Remove — clears any {p.label}-only edits
                        </TooltipContent>
                      </Tooltip>
                      <span aria-hidden className="h-4 w-px bg-border/50" />
                    </>
                  ) : null}
                  <Switch
                    checked={enableMode ? selected && !paused : selected}
                    onCheckedChange={() => {
                      if (enableMode && selected) onToggleEnabled!(p.id);
                      else onTogglePlatform(p.id);
                    }}
                    className={SWITCH_CLS}
                    onClick={(e) => e.stopPropagation()}
                  />
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            openSettings('integrations');
          }}
          className="justify-between gap-2"
        >
          <span className={ROW_LABEL}>Manage all</span>
          <ArrowUpRightIcon
            className={cn(SIDEBAR_POPOVER_ICON, 'text-muted-foreground/70')}
            weight="regular"
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
